import { ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useRoles } from "@/hooks/useRoles";
import { useActiveCoach } from "@/hooks/useActiveCoach";
import { useSocialFeature } from "@/hooks/useSocialFeature";
import { supabase } from "@/integrations/supabase/client";
import { getDb } from "@/lib/offlineDb";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import {
  LayoutDashboard,
  Utensils,
  Dumbbell,
  User,
  LogOut,
  Shield,
  Ruler,
  UserTie,
  Bot,
  Wallet,
} from "@/lib/icons";
import { QrCode, UserCircle, Users as UsersIcon } from "lucide-react";
import logo from "@/assets/benefitness_icon-2.svg";
import { BrandLabel } from "@/components/BrandLabel";
import { OfflineBanner } from "@/components/OfflineBanner";
import { OfflineDebugPanel } from "@/components/OfflineDebugPanel";
import { AdBanner } from "@/components/AdBanner";
import { useAttentionAlerts } from "@/hooks/useAttentionAlerts";
import { useChatNotifications } from "@/hooks/useChatNotifications";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { useFeatureGate } from "@/hooks/useFeatureGate";

const baseNavItems = [
  { path: "/", label: "Home", icon: LayoutDashboard },
  { path: "/tracking", label: "Food", icon: Utensils },
  { path: "/workouts", label: "Workouts", icon: Dumbbell },
  { path: "/body-metrics", label: "Body", icon: Ruler },
];

export function AppLayout({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const { isAdmin, isCoach } = useRoles();
  const { activeCoach } = useActiveCoach();
  const { socialEnabled } = useSocialFeature();
  const location = useLocation();
  const [walletEnabled, setWalletEnabled] = useState(false);
  useAttentionAlerts();
  useChatNotifications();
  const { hasUnreadFriend, hasUnreadCoach } = useUnreadMessages();
  const { canAccess } = useFeatureGate();

  // Load gym_wallet_enabled from profile features_json — re-check on route change
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async () => {
      try {
        const db = await getDb();
        const cached = await db.get("profiles", user.id);
        if (!cancelled && cached) {
          const cachedFeatures = cached.features_json;
          setWalletEnabled(cachedFeatures?.gym_wallet_enabled === true);
        }
      } catch { /* ignore */ }
      try {
        const { data } = await supabase
          .from("profiles")
          .select("features_json")
          .eq("user_id", user.id)
          .maybeSingle();
        if (!cancelled && data) {
          const fj = (data as any).features_json;
          setWalletEnabled(fj?.gym_wallet_enabled === true);
        }
      } catch { /* offline */ }
    };
    load();
    return () => { cancelled = true; };
  }, [user, location.pathname]);

  useEffect(() => {
    const handleProfileFeaturesUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ userId?: string; features_json?: Record<string, any> }>).detail;
      if (!user || detail?.userId !== user.id) return;
      setWalletEnabled(detail?.features_json?.gym_wallet_enabled === true);
    };

    window.addEventListener("profile-features-updated", handleProfileFeaturesUpdated as EventListener);
    return () => window.removeEventListener("profile-features-updated", handleProfileFeaturesUpdated as EventListener);
  }, [user]);

  // Determine which coach nav item to show based on subscription access
  const showHumanCoachNav = activeCoach && canAccess("coach_messaging");
  const showAiCoachNav = !showHumanCoachNav && canAccess("ai_chat");

  const navItems = [
    ...baseNavItems,
    ...(socialEnabled ? [{ path: "/friends", label: "Friends", icon: UsersIcon }] : []),
    ...(showHumanCoachNav ? [{ path: "/my-coach", label: "My Coach", icon: UserCircle }] : []),
    ...(showAiCoachNav ? [{ path: "/ai-coach", label: "AI Coach", icon: Bot }] : []),
    ...(isCoach ? [{ path: "/coach", label: "Coach", icon: UserTie }] : []),
    ...(isAdmin ? [{ path: "/admin", label: "Admin", icon: Shield }] : []),
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Offline banner */}
      <OfflineBanner />

      {/* Desktop top bar */}
      <header className="hidden lg:flex sticky top-0 z-30 bg-card/95 backdrop-blur-md border-b items-center px-4 h-14">
        <Link to="/" className="flex items-center gap-2 mr-4 lg:mr-6 shrink-0">
           <img src={logo} alt="smrtlyFit logo" className="h-8 w-8" />
          <BrandLabel className="font-display text-lg font-bold text-foreground" />
        </Link>

        <TooltipProvider delayDuration={300}>
          <nav className="flex items-center gap-0.5">
            {navItems.map((item) => {
              const active = location.pathname === item.path;
              const showDot =
                (hasUnreadCoach && (item.path === "/coach" || item.path === "/my-coach")) ||
                (hasUnreadFriend && item.path === "/friends");
              return (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    <Link
                      to={item.path}
                      className={`relative flex items-center gap-2 px-2.5 lg:px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        active
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                    >
                      <item.icon className="h-4 w-4" />
                      {showDot && (
                        <span className="absolute top-1 left-[1.1rem] lg:left-[1.3rem] h-2 w-2 rounded-full bg-destructive ring-2 ring-card" />
                      )}
                      <span className="hidden lg:inline">{item.label}</span>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent className="lg:hidden">{item.label}</TooltipContent>
                </Tooltip>
              );
            })}
          </nav>
        </TooltipProvider>

        <div className="ml-auto flex items-center gap-2">
          <p className="text-sm text-muted-foreground truncate max-w-[180px] hidden lg:block">{user?.email}</p>
            {walletEnabled && (
              <Link to="/gym-wallet">
                <IconButton variant="ghost" tooltip="Gym Pass" className={`shrink-0 h-8 w-8 ${location.pathname === "/gym-wallet" ? "text-primary" : ""}`}>
                  <QrCode className="h-4 w-4" />
                </IconButton>
              </Link>
            )}
              <Link to="/profile">
                <IconButton variant="ghost" tooltip="Profile" className={`shrink-0 h-8 w-8 ${location.pathname === "/profile" ? "text-primary" : ""}`}>
                  <User className="h-4 w-4" />
                </IconButton>
              </Link>
              <IconButton variant="ghost" tooltip="Sign out" onClick={signOut} className="shrink-0 h-8 w-8">
                <LogOut className="h-4 w-4" />
              </IconButton>
        </div>
      </header>

      {/* Mobile / tablet top bar */}
      <header className="lg:hidden sticky top-0 z-20 bg-card backdrop-blur-md border-b px-4 pb-3 flex items-center justify-center gap-3 safe-top" style={{ '--safe-top-extra': '0.75rem' } as React.CSSProperties}>
        <img src={logo} alt="smrtlyFit logo" className="h-8 w-8" />
        <BrandLabel className="font-display text-lg font-bold text-foreground flex-1" />
        {walletEnabled && (
          <Link to="/gym-wallet">
            <IconButton variant="ghost" tooltip="Gym Pass" className={`h-9 w-9 ${location.pathname === "/gym-wallet" ? "text-primary" : ""}`}>
              <QrCode className="h-4 w-4" />
            </IconButton>
          </Link>
        )}
        <Link to="/profile">
          <IconButton variant="ghost" tooltip="Profile" className={`h-9 w-9 ${location.pathname === "/profile" ? "text-primary" : ""}`}>
            <User className="h-4 w-4" />
          </IconButton>
        </Link>
        <IconButton variant="ghost" tooltip="Sign out" onClick={signOut} className="h-9 w-9">
          <LogOut className="h-4 w-4" />
        </IconButton>
      </header>

      {/* Page content */}
      <main className="flex-1 p-4 lg:p-8 pb-safe-nav lg:pb-8 max-w-6xl w-full mx-auto space-y-4">
        <AdBanner />
        {children}
      </main>

      {/* Mobile / tablet bottom tab bar */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-card/95 backdrop-blur-md border-t safe-bottom">
        <div className="flex items-center justify-evenly px-1 py-1">
          {navItems.map((item) => {
            const active = location.pathname === item.path;
            const showDot =
              (hasUnreadCoach && (item.path === "/coach" || item.path === "/my-coach")) ||
              (hasUnreadFriend && item.path === "/friends");
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`relative flex flex-col items-center gap-0.5 py-2 px-1.5 rounded-lg min-w-0 flex-1 transition-colors active:scale-95 ${
                  active ? "text-primary" : "text-muted-foreground"
                }`}
              >
                <item.icon className={`h-5 w-5 shrink-0 ${active ? "stroke-[2.5]" : ""}`} />
                {showDot && (
                  <span className="absolute top-1.5 right-[calc(50%-0.1rem)] translate-x-[0.6rem] h-2 w-2 rounded-full bg-destructive ring-2 ring-card" />
                )}
                <span className={`text-[10px] font-medium truncate max-w-full ${active ? "font-semibold" : ""}`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Dev-only debug panel */}
      <OfflineDebugPanel />
    </div>
  );
}
